/**
 * PromptsEditor — gestion versionada de prompts del Catalogador IA.
 *
 * Endpoints:
 *   GET    /api/qa/prompts?type=queryai|aimatcher
 *   GET    /api/qa/prompts/:id   (content completo)
 *   POST   /api/qa/prompts       body {prompt_type, version, content, notes, activate}
 *   POST   /api/qa/prompts/:id/activate
 *   DELETE /api/qa/prompts/:id   (no permite si is_active)
 *
 * UI:
 *   - Selector de type (queryai | aimatcher) arriba.
 *   - Tabla de versiones con badge is_active, content_length, created_at, created_by, acciones.
 *   - Boton "Nueva version" → modal con form.
 *   - Boton "Ver/Editar" por fila → modal con textarea (fetch /:id para content full).
 *   - Boton "Activar" en filas inactivas.
 *   - Boton "Eliminar" en filas inactivas (con confirm).
 *   - Validacion: warning si content < 100 chars.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import './PromptsEditor.css';

const VALID_TYPES_FALLBACK = ['queryai', 'aimatcher'];
const MIN_CONTENT_LENGTH = 100;

function fmtDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch (_) {
    return '—';
  }
}

function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className={`um-toast ${toast.type === 'error' ? 'um-toast-error' : ''}`}>
      {toast.message}
    </div>
  );
}

function NewPromptModal({ promptType, validTypes, busy, onClose, onSubmit }) {
  const [type, setType] = useState(promptType);
  const [version, setVersion] = useState('');
  const [content, setContent] = useState('');
  const [notes, setNotes] = useState('');
  const [activate, setActivate] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!version.trim()) {
      setFormError('Version es requerida (ej: v1.0, 2026-06-05).');
      return;
    }
    if (!content.trim()) {
      setFormError('El contenido es requerido.');
      return;
    }
    try {
      await onSubmit({
        prompt_type: type,
        version: version.trim(),
        content,
        notes: notes.trim() || null,
        activate: !!activate,
      });
    } catch (err) {
      setFormError(err.message || 'Error al crear.');
    }
  };

  const lenWarning = content.length > 0 && content.length < MIN_CONTENT_LENGTH;

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal qa-modal-lg" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="um-modal-header">
            <h3>Nueva version de prompt</h3>
          </div>
          <div className="um-modal-body">
            {formError && <div className="um-error">{formError}</div>}
            <div className="qa-form-row">
              <div className="um-field">
                <label>Tipo</label>
                <select value={type} onChange={(e) => setType(e.target.value)}>
                  {validTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="um-field">
                <label>Version</label>
                <input
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="v1.0"
                  required
                />
              </div>
            </div>
            <div className="um-field">
              <label>
                Contenido
                <span className="qa-field-meta">
                  {content.length.toLocaleString('es-AR')} chars
                  {lenWarning && (
                    <span className="qa-warning-inline"> — corto, podria ser placeholder</span>
                  )}
                </span>
              </label>
              <textarea
                className="qa-textarea"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={16}
                placeholder="Contenido completo del prompt..."
                required
              />
            </div>
            <div className="um-field">
              <label>Notas</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas opcionales sobre esta version"
              />
            </div>
            <div className="um-field um-checkbox-row">
              <input
                id="qa-activate"
                type="checkbox"
                checked={activate}
                onChange={(e) => setActivate(e.target.checked)}
              />
              <label htmlFor="qa-activate">Activar al crear (desactiva otras del mismo tipo)</label>
            </div>
          </div>
          <div className="um-modal-footer">
            <button type="button" className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className="um-btn um-btn-primary" disabled={busy}>
              {busy ? 'Creando...' : 'Crear version'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ViewPromptModal({ prompt, loading, onClose }) {
  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal qa-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="um-modal-header">
          <h3>
            {prompt?.prompt_type} {prompt?.version}
            {prompt?.is_active && <span className="qa-badge qa-badge-active" style={{ marginLeft: 8 }}>activo</span>}
          </h3>
        </div>
        <div className="um-modal-body">
          {loading ? (
            <div className="um-loading">Cargando contenido...</div>
          ) : (
            <>
              <div className="qa-meta-row">
                <div><strong>Notas:</strong> {prompt?.notes || '—'}</div>
                <div><strong>Creado:</strong> {fmtDateTime(prompt?.created_at)} por {prompt?.created_by || '—'}</div>
                <div><strong>Longitud:</strong> {(prompt?.content?.length || prompt?.content_length || 0).toLocaleString('es-AR')} chars</div>
              </div>
              <textarea
                className="qa-textarea"
                value={prompt?.content || ''}
                rows={20}
                readOnly
              />
            </>
          )}
        </div>
        <div className="um-modal-footer">
          <button className="um-btn um-btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirm({ prompt, busy, onClose, onConfirm }) {
  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={(e) => e.stopPropagation()}>
        <div className="um-modal-header">
          <h3>Eliminar version</h3>
        </div>
        <div className="um-modal-body">
          <p>
            Vas a eliminar <strong>{prompt?.prompt_type} {prompt?.version}</strong>.
            Esta accion no se puede deshacer.
          </p>
        </div>
        <div className="um-modal-footer">
          <button className="um-btn um-btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="um-btn um-btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PromptsEditor() {
  const [promptType, setPromptType] = useState('queryai');
  const [validTypes, setValidTypes] = useState(VALID_TYPES_FALLBACK);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [newModal, setNewModal] = useState({ open: false, busy: false });
  const [viewModal, setViewModal] = useState({ open: false, prompt: null, loading: false });
  const [deleteState, setDeleteState] = useState({ prompt: null, busy: false });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/qa/prompts?type=${encodeURIComponent(promptType)}`);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Sin permisos para ver prompts (se requiere superadmin).');
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data.data) ? data.data : []);
      if (Array.isArray(data.valid_types) && data.valid_types.length > 0) {
        setValidTypes(data.valid_types);
      }
    } catch (err) {
      console.error('[PromptsEditor] fetch error:', err);
      setError(err.message || 'Error al cargar prompts');
    } finally {
      setLoading(false);
    }
  }, [promptType]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleCreate = async (payload) => {
    setNewModal(s => ({ ...s, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/qa/prompts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 401 || res.status === 403) throw new Error('Sin permisos.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      setNewModal({ open: false, busy: false });
      showToast(`Prompt ${payload.prompt_type} ${payload.version} creado`);
      // si lo creo bajo otro type, switch al type creado
      if (payload.prompt_type !== promptType) {
        setPromptType(payload.prompt_type);
      } else {
        await fetchRows();
      }
    } catch (err) {
      setNewModal(s => ({ ...s, busy: false }));
      throw err;
    }
  };

  const openView = async (row) => {
    setViewModal({ open: true, prompt: { ...row, content: '' }, loading: true });
    try {
      const res = await fetch(`${API_BASE}/api/qa/prompts/${encodeURIComponent(row.id)}`);
      if (res.status === 401 || res.status === 403) throw new Error('Sin permisos.');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const full = data?.data || data;
      setViewModal({ open: true, prompt: full, loading: false });
    } catch (err) {
      showToast(err.message || 'Error al cargar contenido', 'error');
      setViewModal({ open: false, prompt: null, loading: false });
    }
  };

  const handleActivate = async (row) => {
    try {
      const res = await fetch(`${API_BASE}/api/qa/prompts/${encodeURIComponent(row.id)}/activate`, {
        method: 'POST',
      });
      if (res.status === 401 || res.status === 403) throw new Error('Sin permisos.');
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      showToast(`Version ${row.version} activada`);
      await fetchRows();
    } catch (err) {
      showToast(err.message || 'Error al activar', 'error');
    }
  };

  const handleDelete = async () => {
    const row = deleteState.prompt;
    if (!row) return;
    setDeleteState(s => ({ ...s, busy: true }));
    try {
      const res = await fetch(`${API_BASE}/api/qa/prompts/${encodeURIComponent(row.id)}`, {
        method: 'DELETE',
      });
      if (res.status === 401 || res.status === 403) {
        showToast('Sin permisos para eliminar.', 'error');
      } else if (!res.ok) {
        const j = await res.json().catch(() => null);
        showToast(j?.error || `HTTP ${res.status}`, 'error');
      } else {
        showToast(`Version ${row.version} eliminada`);
        await fetchRows();
      }
    } catch (err) {
      showToast(err.message || 'Error al eliminar', 'error');
    } finally {
      setDeleteState({ prompt: null, busy: false });
    }
  };

  return (
    <>
      <div className="um-card">
        <div className="um-toolbar">
          <div className="um-toolbar-left">
            <label className="qa-toolbar-label">Tipo:</label>
            <select
              className="qa-select"
              value={promptType}
              onChange={(e) => setPromptType(e.target.value)}
            >
              {validTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <span className="um-count">
              {loading ? '...' : `${rows.length} version${rows.length === 1 ? '' : 'es'}`}
            </span>
          </div>
          <div>
            <button
              className="um-btn um-btn-ghost"
              onClick={fetchRows}
              disabled={loading}
              style={{ marginRight: 8 }}
            >
              Refrescar
            </button>
            <button
              className="um-btn um-btn-primary"
              onClick={() => setNewModal({ open: true, busy: false })}
            >
              + Nueva version
            </button>
          </div>
        </div>

        {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}

        <div className="um-table-wrap">
          {loading ? (
            <div className="um-loading">Cargando prompts...</div>
          ) : rows.length === 0 ? (
            <div className="um-empty">No hay versiones de "{promptType}".</div>
          ) : (
            <table className="um-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Estado</th>
                  <th>Long.</th>
                  <th>Notas</th>
                  <th>Creado</th>
                  <th>Por</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const isShort = (r.content_length || 0) < MIN_CONTENT_LENGTH;
                  return (
                    <tr key={r.id}>
                      <td><strong>{r.version}</strong></td>
                      <td>
                        {r.is_active
                          ? <span className="qa-badge qa-badge-active">activo</span>
                          : <span className="qa-badge qa-badge-inactive">inactivo</span>}
                      </td>
                      <td>
                        <span className={isShort ? 'qa-len-warn' : ''}>
                          {(r.content_length || 0).toLocaleString('es-AR')}
                          {isShort && <span title="Posible placeholder"> ⚠</span>}
                        </span>
                      </td>
                      <td className="qa-cell-notes" title={r.notes || ''}>{r.notes || '—'}</td>
                      <td>{fmtDateTime(r.created_at)}</td>
                      <td>{r.created_by || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="um-btn um-btn-link" onClick={() => openView(r)}>
                          Ver
                        </button>
                        {!r.is_active && (
                          <>
                            <button className="um-btn um-btn-link" onClick={() => handleActivate(r)}>
                              Activar
                            </button>
                            <button
                              className="um-btn um-btn-link-danger"
                              onClick={() => setDeleteState({ prompt: r, busy: false })}
                            >
                              Eliminar
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {newModal.open && (
        <NewPromptModal
          promptType={promptType}
          validTypes={validTypes}
          busy={newModal.busy}
          onClose={() => newModal.busy ? null : setNewModal({ open: false, busy: false })}
          onSubmit={handleCreate}
        />
      )}

      {viewModal.open && (
        <ViewPromptModal
          prompt={viewModal.prompt}
          loading={viewModal.loading}
          onClose={() => setViewModal({ open: false, prompt: null, loading: false })}
        />
      )}

      {deleteState.prompt && (
        <DeleteConfirm
          prompt={deleteState.prompt}
          busy={deleteState.busy}
          onClose={() => deleteState.busy ? null : setDeleteState({ prompt: null, busy: false })}
          onConfirm={handleDelete}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
